/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowForward as NextIcon,
  ArrowBack as PrevIcon,
  Check as CheckIcon,
  PersonOutline as PersonIcon,
  SkipNext as SkipIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import { PatientPicker } from '@/components/PatientPicker';
import { useActivePatient } from '@/utils/activePatient';
import {
  Description as PrescriptionIcon,
  Healing as DischargeIcon,
  HealthAndSafety as WellnessIcon,
  LocalHospital as OPIcon,
  Receipt as InvoiceIcon,
  Science as DiagnosticIcon,
  UploadFile as DocIcon,
  Vaccines as ImmunizationIcon,
} from '@mui/icons-material';
import useSWR from 'swr';
import { PageShell } from '@/layout/PageShell';
import { StatusPill } from '@/components/StatusPill';
import { useAuth } from '@/auth/AuthContext';
import { useToast } from '@/components/Toaster';
import { explorer } from '@/api/explorer';
import { me } from '@/api/tenant';
import type { ConsultationPushBody, FhirBundleType } from '@/api/clinical';
import {
  PrescriptionForm,
  OPConsultationForm,
  DiagnosticReportForm,
  HealthDocumentForm,
  WellnessRecordForm,
  ImmunizationForm,
  DischargeSummaryForm,
  InvoiceForm,
  bundleTypeName,
} from './M2ConsultationForm';

/**
 * FHIR Bundles sequential wizard. Eight steps in care-flow order
 * (registration → discharge), with a per-step status (pending /
 * pushed / failed / skipped). Each step embeds the matching FHIR-aware
 * sub-form. Tenant developers walk the entire HIP push lifecycle
 * end-to-end for sandbox certification, then take the captured
 * payloads + integration kit into their HIMS.
 *
 * Common patient + encounter context lives at the wizard level so all
 * eight bundles share the same memberId + ABHA + appointmentDate.
 * That's how a real HIP push works: one visit → multiple bundles.
 */

type StepStatus = 'pending' | 'pushed' | 'failed' | 'skipped';

interface WizardStep {
  type: FhirBundleType;
  label: string;
  helperText: string;
  icon: ComponentType<SvgIconProps>;
}

const STEPS: WizardStep[] = [
  { type: 'OPConsultation',       label: 'OP Consultation',     helperText: 'Visit start. ICD-10 + complaints + exam.',           icon: OPIcon },
  { type: 'Prescription',         label: 'Prescription',        helperText: 'Medications + dosage + intake.',                     icon: PrescriptionIcon },
  { type: 'DiagnosticReport',     label: 'Diagnostic Report',   helperText: 'Lab/Radiology results + impression.',                icon: DiagnosticIcon },
  { type: 'HealthDocumentRecord', label: 'Health Document',     helperText: 'Free-form clinical document (PDF).',                 icon: DocIcon },
  { type: 'WellnessRecord',       label: 'Wellness Record',     helperText: 'Vitals + body measurements + lifestyle.',            icon: WellnessIcon },
  { type: 'Immunization',         label: 'Immunization',        helperText: 'Vaccine + lot + dose + route.',                      icon: ImmunizationIcon },
  { type: 'Invoice',              label: 'Invoice',             helperText: 'Items + GST + total.',                               icon: InvoiceIcon },
  { type: 'DischargeSummary',     label: 'Discharge Summary',   helperText: 'Visit end. Diagnoses + procedures + follow-ups.',    icon: DischargeIcon },
];

interface CommonCtx {
  tenantId: string;
  memberId: string;
  abhaAddress: string;
  abhaNumber: string;
  patientName: string;
  patientGender: string;
  patientBirthDate: string;
  facilityName: string;
  facilityId: string;
  practitionerName: string;
  practitionerId: string;
  appointmentDate: string;
}

export function IntegrationFhirPage() {
  const theme = useTheme();
  const toast = useToast();
  const { claims } = useAuth();
  const { data: tenantSelf } = useSWR('me-tenant', () => me.tenant());
  const [patient] = useActivePatient();
  const [pickerOpen, setPickerOpen] = useState(false);

  const now = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  const [ctx, setCtx] = useState<CommonCtx>({
    tenantId: '',
    memberId: '',
    abhaAddress: '',
    abhaNumber: '',
    patientName: '',
    patientGender: 'unknown',
    patientBirthDate: '',
    facilityName: '',
    facilityId: '',
    practitionerName: '',
    practitionerId: '',
    appointmentDate: now,
  });

  // Pull from the active patient whenever it changes — auto-fills the
  // visit context so the tenant developer doesn't re-type ABHA / UHID.
  useEffect(() => {
    if (!patient) return;
    setCtx((c) => ({
      ...c,
      memberId: patient.memberId,
      abhaAddress: patient.abhaAddress,
      abhaNumber: (patient.abhaNumber ?? '').replace(/-/g, ''),
      patientName: patient.name,
      patientGender: (patient.gender ?? 'unknown').toLowerCase(),
      patientBirthDate: patient.birthDate || '',
    }));
  }, [patient]);
  const [statuses, setStatuses] = useState<Record<FhirBundleType, StepStatus>>(() =>
    STEPS.reduce((acc, s) => ({ ...acc, [s.type]: 'pending' as StepStatus }), {} as Record<FhirBundleType, StepStatus>),
  );
  const [results, setResults] = useState<Record<FhirBundleType, unknown>>({} as Record<FhirBundleType, unknown>);
  const [active, setActive] = useState(0);
  const [running, setRunning] = useState(false);
  const [contextLocked, setContextLocked] = useState(false);

  // Seed context from tenant + JWT once tenantSelf resolves
  if (tenantSelf && !ctx.tenantId) {
    setCtx((c) => ({
      ...c,
      tenantId: tenantSelf.tenantId,
      facilityName: tenantSelf.organizationName ?? '',
      facilityId: (tenantSelf as unknown as { hfrId?: string }).hfrId ?? tenantSelf.tenantId ?? '',
      practitionerName: String(claims?.name ?? claims?.username ?? ''),
      practitionerId: String(claims?.username ?? ''),
    }));
  }

  const activeStep = STEPS[active]!;
  // Stable per session — generated once so all 8 bundles share the same base
  // (matches old connector's pattern of one visit → one shared consultation context).
  const consultationBase = useRef(`${ctx.memberId || 'VISIT'}-${new Date().getTime().toString(36).toUpperCase()}`);
  const sharedConsultationNumber = consultationBase.current;

  const buildEnvelope = (clinical: Record<string, unknown>, type: FhirBundleType): ConsultationPushBody => ({
    tenantId: ctx.tenantId,
    memberId: ctx.memberId,
    consultationNumber: `${sharedConsultationNumber}-${type.slice(0, 4).toUpperCase()}`,
    status: 'CLOSED',
    appointmentDate: ctx.appointmentDate,
    linkedToABDM: true,
    abhaAddress: ctx.abhaAddress,
    abhaNumber: ctx.abhaNumber,
    fhirBundleType: type,
    clinical_data: {
      bundleType: bundleTypeName(type),
      careContextReference: `${sharedConsultationNumber}-${type.slice(0, 4).toUpperCase()}`,
      encounter: `OPD-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${randomSlug(4)}`,
      patient: {
        name: ctx.patientName,
        patientReference: ctx.abhaAddress,
        gender: ctx.patientGender,
        ...(ctx.patientBirthDate ? { birthDate: ctx.patientBirthDate } : {}),
      },
      practitioners: [{ name: ctx.practitionerName, practitionerId: ctx.practitionerId }],
      organisation: { facilityName: ctx.facilityName, facilityId: ctx.facilityId },
      visitDate: ctx.appointmentDate,
      documents: [],
      ...clinical,
    },
  });

  const pushBundle = async (clinical: Record<string, unknown>, type: FhirBundleType) => {
    const body = buildEnvelope(clinical, type);
    setRunning(true);
    try {
      const r = await explorer.run({ apiCode: 'M2.CONSULTATION.PUSH', body: body as unknown as Record<string, unknown> });
      setResults((p) => ({ ...p, [type]: r.response }));
      setStatuses((p) => ({ ...p, [type]: r.ok ? 'pushed' : 'failed' }));
      if (r.ok) {
        toast.success(`${labelOf(type)} pushed · ${r.httpStatus} · ${r.durationMs}ms`);
        // auto-advance on success
        if (active < STEPS.length - 1) setActive(active + 1);
      } else {
        toast.error(`${labelOf(type)} failed · ${r.httpStatus}`, r.decodedError ?? 'See response panel.');
      }
    } catch (e) {
      setStatuses((p) => ({ ...p, [type]: 'failed' }));
      toast.error('Push failed', (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const skip = () => {
    setStatuses((p) => ({ ...p, [activeStep.type]: 'skipped' }));
    if (active < STEPS.length - 1) setActive(active + 1);
  };

  return (
    <PageShell
      kicker="TENANT · INTEGRATION · FHIR BUNDLES"
      title="HIP Push Wizard"
      subtitle="Walk the eight FHIR R4 bundle types in care-flow order — registration through discharge — for sandbox certification. Captured payloads become your HIMS integration template."
      width="full"
      actions={tenantSelf && (
        <StatusPill tone={tenantSelf.environment === 'PRODUCTION' ? 'success' : 'info'}>
          {tenantSelf.environment}
        </StatusPill>
      )}
    >
      {!contextLocked && (
        <Box sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Stack spacing={0.25}>
              <Typography variant="overline" color="text.tertiary">VISIT CONTEXT</Typography>
              <Typography variant="body2" color="text.secondary">
                Set once for the whole visit — every FHIR bundle below shares this patient + practitioner + facility.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<PersonIcon />} onClick={() => setPickerOpen(true)}>
                {patient ? 'Change patient' : 'Pick patient'}
              </Button>
              <Button variant="contained" disabled={!ctx.memberId || !ctx.abhaAddress || !ctx.patientName} onClick={() => setContextLocked(true)}>
                Lock context & start
              </Button>
            </Stack>
          </Stack>
          {patient && (
            <Alert
              severity="success" variant="outlined"
              sx={{ mb: 2, borderRadius: theme.radius.sm + 'px' }}
              action={<Button size="small" startIcon={<SwapIcon fontSize="small" />} onClick={() => setPickerOpen(true)}>Change</Button>}
            >
              <Typography variant="body2" fontWeight={500}>Patient: {patient.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {patient.abhaAddress} · UHID {patient.memberId} · {patient.gender ?? '—'} · {patient.birthDate ?? '—'}
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <TextField size="small" label="UHID / memberId *" value={ctx.memberId} onChange={(e) => setCtx({ ...ctx, memberId: e.target.value })} helperText="From your patient master" />
            <TextField size="small" label="Patient name *" value={ctx.patientName} onChange={(e) => setCtx({ ...ctx, patientName: e.target.value })} />
            <TextField size="small" select label="Gender *" value={ctx.patientGender} onChange={(e) => setCtx({ ...ctx, patientGender: e.target.value })}>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </TextField>
            <TextField
              size="small" type="date" label="Date of birth"
              InputLabelProps={{ shrink: true }}
              value={ctx.patientBirthDate}
              onChange={(e) => setCtx({ ...ctx, patientBirthDate: e.target.value })}
              helperText="DD/MM/YYYY"
            />
            <TextField size="small" label="ABHA address *" value={ctx.abhaAddress} onChange={(e) => setCtx({ ...ctx, abhaAddress: e.target.value })} />
            <TextField size="small" label="ABHA number" value={ctx.abhaNumber} onChange={(e) => setCtx({ ...ctx, abhaNumber: e.target.value })} />
            <TextField
              size="small" label="Tenant ID"
              value={ctx.tenantId}
              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Chip label="Auto" size="small" color="success" sx={{ height: 18, fontSize: 10 }} /></InputAdornment> }}
              helperText="Set automatically from your login"
            />
            <TextField
              size="small" label="Facility name"
              value={ctx.facilityName}
              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Chip label="Auto" size="small" color="success" sx={{ height: 18, fontSize: 10 }} /></InputAdornment> }}
              helperText="From tenant profile"
            />
            <TextField
              size="small" label="Facility / HIP ID"
              value={ctx.facilityId}
              InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Chip label="Auto" size="small" color="success" sx={{ height: 18, fontSize: 10 }} /></InputAdornment> }}
              helperText="From tenant ABDM config"
            />
            <TextField size="small" label="Practitioner name" value={ctx.practitionerName} onChange={(e) => setCtx({ ...ctx, practitionerName: e.target.value })} helperText="Default: your login name" />
            <TextField size="small" label="Practitioner ID" value={ctx.practitionerId} onChange={(e) => setCtx({ ...ctx, practitionerId: e.target.value })} helperText="MCI / state council reg #" />
            <TextField
              size="small" type="datetime-local" label="Appointment date & time"
              InputLabelProps={{ shrink: true }}
              value={ctx.appointmentDate.slice(0, 16)}
              onChange={(e) => setCtx({ ...ctx, appointmentDate: new Date(e.target.value).toISOString().replace(/\.\d{3}Z$/, 'Z') })}
              helperText="DD/MM/YYYY HH:MM"
            />
          </Box>
        </Box>
      )}

      {contextLocked && (
        <>
          {/* Stepper rail */}
          <Box sx={{ display: 'flex', overflowX: 'auto', mb: 3, borderBottom: `1px solid ${theme.palette.border.subtle}`, pb: 2 }}>
            {STEPS.map((s, i) => {
              const status = statuses[s.type];
              const isActive = i === active;
              const tone = status === 'pushed' ? theme.palette.status.success
                : status === 'failed' ? theme.palette.status.danger
                : status === 'skipped' ? theme.palette.text.tertiary
                : isActive ? theme.palette.brand.main
                : theme.palette.surface.sunken;
              return (
                <Box
                  key={s.type}
                  onClick={() => setActive(i)}
                  sx={{
                    minWidth: 130,
                    cursor: 'pointer',
                    px: 1.5, py: 1.25, mr: 1,
                    borderRadius: theme.radius.sm + 'px',
                    bgcolor: isActive ? theme.palette.brand.lighter : 'transparent',
                    borderLeft: `3px solid ${tone}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: status === 'pushed' ? theme.palette.status.success : 'transparent', color: status === 'pushed' ? '#fff' : 'inherit', display: 'grid', placeItems: 'center', border: `1px solid ${tone}` }}>
                      {status === 'pushed' ? <CheckIcon sx={{ fontSize: 14 }} /> : <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>{i + 1}</Typography>}
                    </Box>
                    <Stack spacing={0}>
                      <Typography variant="caption" fontWeight={isActive ? 600 : 400} sx={{ fontSize: 12 }}>{s.label}</Typography>
                      <Typography variant="caption" color="text.tertiary" sx={{ fontSize: 9.5 }}>
                        {status === 'pushed' ? 'Pushed' : status === 'failed' ? 'Failed' : status === 'skipped' ? 'Skipped' : 'Pending'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>

          {/* Active step content */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' }, gap: 3 }}>
            <Box sx={{ p: 3, border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <activeStep.icon sx={{ fontSize: 22, color: theme.palette.brand.dark }} />
                <Typography variant="h2" sx={{ fontSize: 18, fontWeight: 600 }}>Step {active + 1} of {STEPS.length} · {activeStep.label}</Typography>
                <Chip size="small" label="FHIR R4" color="info" variant="outlined" />
                <Chip size="small" label="ABDM PHR linked" color="success" variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{activeStep.helperText}</Typography>
              <BundleSubForm type={activeStep.type} onRun={(c) => pushBundle(c, activeStep.type)} submitting={running} />

              <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.border.subtle}` }}>
                <Button startIcon={<PrevIcon />} disabled={active === 0} onClick={() => setActive(active - 1)}>Previous</Button>
                <Stack direction="row" spacing={1}>
                  <Button startIcon={<SkipIcon />} onClick={skip} disabled={active === STEPS.length - 1}>Skip</Button>
                  <Button endIcon={<NextIcon />} variant="outlined" disabled={active === STEPS.length - 1} onClick={() => setActive(active + 1)}>Next</Button>
                </Stack>
              </Stack>
            </Box>

            {/* Side rail: progress + last response */}
            <Stack spacing={2}>
              <Box sx={{ p: 2, border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
                <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>VISIT</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{ctx.patientName}</Typography>
                <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace', display: 'block' }}>{ctx.abhaAddress}</Typography>
                <Typography variant="caption" color="text.tertiary" sx={{ display: 'block', mt: 0.5 }}>UHID: {ctx.memberId}</Typography>
                <Button size="small" onClick={() => setContextLocked(false)} sx={{ mt: 1 }}>Edit context</Button>
              </Box>
              {results[activeStep.type] != null && (
                <Box sx={{ p: 2, border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="overline" color="text.tertiary">LATEST RESPONSE</Typography>
                    <StatusPill tone={statuses[activeStep.type] === 'pushed' ? 'success' : 'danger'}>
                      {statuses[activeStep.type] === 'pushed' ? 'Pushed' : 'Failed'}
                    </StatusPill>
                  </Stack>
                  <Box component="pre" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, bgcolor: theme.palette.surface.sunken, p: 1.5, m: 0, borderRadius: theme.radius.sm + 'px', maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(results[activeStep.type], null, 2)}
                  </Box>
                </Box>
              )}
              {Object.values(statuses).every((s) => s !== 'pending') && (
                <Alert severity="success" variant="outlined">
                  All eight bundles processed. {Object.values(statuses).filter((s) => s === 'pushed').length} pushed,{' '}
                  {Object.values(statuses).filter((s) => s === 'skipped').length} skipped,{' '}
                  {Object.values(statuses).filter((s) => s === 'failed').length} failed. Take the working payloads to your HIMS via the API Explorer's Integration Kit.
                </Alert>
              )}
            </Stack>
          </Box>
        </>
      )}

      <PatientPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </PageShell>
  );
}

function BundleSubForm({ type, onRun, submitting }: { type: FhirBundleType; onRun: (c: Record<string, unknown>) => void; submitting: boolean }) {
  switch (type) {
    case 'OPConsultation':       return <OPConsultationForm onRun={onRun} submitting={submitting} />;
    case 'Prescription':         return <PrescriptionForm onRun={onRun} submitting={submitting} />;
    case 'DiagnosticReport':     return <DiagnosticReportForm onRun={onRun} submitting={submitting} />;
    case 'HealthDocumentRecord': return <HealthDocumentForm onRun={onRun} submitting={submitting} />;
    case 'WellnessRecord':       return <WellnessRecordForm onRun={onRun} submitting={submitting} />;
    case 'Immunization':         return <ImmunizationForm onRun={onRun} submitting={submitting} />;
    case 'Invoice':              return <InvoiceForm onRun={onRun} submitting={submitting} />;
    case 'DischargeSummary':     return <DischargeSummaryForm onRun={onRun} submitting={submitting} />;
  }
}

function labelOf(type: FhirBundleType): string {
  return STEPS.find((s) => s.type === type)?.label ?? type;
}

function randomSlug(len: number): string {
  return Math.random().toString(36).slice(2, 2 + len).toUpperCase();
}
